/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {Timeslot} from "types/entities/Timeslot";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "helpers/reducers";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {ClockIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";

import {fakeTimeslotMinute, getPackageRespectsTimeslotSize, useClearUpsells} from "utils/packageUtils";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import BookingTimeslot from "pages/Front/Booking/components/BookingTimeslots/BookingTimeslot";
import useSellerTimeslots from "utils/hooks/useSellerTimeslots";
import BookingCardTitle from "pages/Front/Booking/components/BookingCardTitle";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import {eventCategories} from "helpers/config";

export interface BookingTimeslotParams {
    package_id: string;
}

interface Props extends RouteComponentProps<BookingTimeslotParams> {
    selectTimeslotHandler: (timeslot: Timeslot) => void;
    finishHandler: () => void;
    className?: string;
    seller_id: number;
}

const SellerTimeslots = (props: Props) => {
    const {t} = useTranslation();
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const [lastBookingTimeslotSize, setLastBookingTimeslotSize] = useState(16);
    const [skeletonArray, setSkeletonArray] = useState<any[]>([]);
    //the timeslot that is selected in the cart or by automatic selection
    const packageRespectsTimeslotSize = selectedPackage && getPackageRespectsTimeslotSize(selectedPackage);
    const selectedPackageCategory = useTypedSelector(state => state.bookingState.bookingProcess.filter.category);

    const lastMidnight = new Date();
    lastMidnight.setHours(0, 0, 0, 0);

    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);

    const clearPackagesHook = useClearUpsells(bookingProcess);

    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);
    const blockedClass = "bg-gray-600 pointer-events-none opacity-50";

    const {
        prependOoohWarningToBooking,
        bookWithAutomaticTimeslot,
        customSelectedTimeslots,
        firstPossibleSlotIndex,
        isFetchingTimeslot,
        isLoadingTimeslots,
        lastPossibleSlotIndex,
        possibleTimeslots,
        selectCustomSlotsEnabled,
        selectedTimeslot,
        setSelectedTimeslotHandler,
        timeslotsAvailable,
    } = useSellerTimeslots(
        props.selectTimeslotHandler,
        props.history,
        [scrollRefTimeslots, scrollRefCustomTimeslots, scrollRefConfirmTimeslots],
        props.finishHandler,
        props.seller_id
    );

    useEffect(() => {
        clearPackagesHook.clearInvalidUpsells();
    }, [selectedUpsells]);

    useEffect(() => {
        setSkeletonArray(new Array(lastBookingTimeslotSize).fill(""));
    }, [lastBookingTimeslotSize]);

    useEffect(() => {
        setLastBookingTimeslotSize(timeslotsAvailable.length);
    }, [timeslotsAvailable]);

    return (
        // <BookingCard className={clsx("col-span-1 md:col-span-6 lg:col-span-4 xl:col-span-4 !px-3 !py-2.5 xl:!px-3 xl:!py-3", props.className)}>
        <BookingCard className={clsx("col-span-1 md:col-span-6 lg:col-span-4 xl:col-span-4 !p-0", props.className)}>
            <BookingCardTitle className={"px-3 py-2.5 xl:px-3 xl:py-3 border-b"} title={"Timeslots"} />
            <div
                className={clsx(
                    "BookingTimeslots flex flex-col w-full h-full flex-grow justify-between items-center text-center select-none relative px-3 py-2"
                )}
            >
                <div className={"w-full"}>
                    <div className={"relative grow justify-start flex flex-col w-full overflow-y-auto"}>
                        <div
                            ref={scrollRefTimeslots}
                            className={clsx(
                                "timeslots relative grid w-full grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 justify-center items-center gap-4 mx-auto",
                                !isLoadingTimeslots && timeslotsAvailable.length === 0 ? "" : "py-2 px-1"
                            )}
                            id="BookingTimeslots"
                        >
                            {isLoadingTimeslots &&
                                skeletonArray.map((i, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className={
                                                "w-full min-w-[88px] h-[50px] sm:h-[62px] border border-solid border-gray-800/20  overflow-hidden rounded-lg flex leading-[1]"
                                            }
                                        >
                                            <Skeleton containerClassName={"flex-1"} height={"100%"} width={"100%"} />
                                        </div>
                                    );
                                })}
                            {!isLoadingTimeslots &&
                                timeslotsAvailable.map((timeslot: Timeslot, index: number) => {
                                    const possibleTimeslotIndex = possibleTimeslots.findIndex(possible => possible.id === timeslot.id);
                                    const customTimeslotIndex = customSelectedTimeslots
                                        .sort((a, b) => (a.id > b.id ? 1 : -1))
                                        .findIndex(customTimeslot => customTimeslot.id === timeslot.id);
                                    return (
                                        <BookingTimeslot
                                            key={index}
                                            customTimeslotIndex={customTimeslotIndex}
                                            fakeTimeslotMinute={fakeTimeslotMinute(selectedPackage)}
                                            firstPossibleSlotIndex={firstPossibleSlotIndex}
                                            index={index}
                                            lastPossibleSlotIndex={lastPossibleSlotIndex}
                                            packageRespectsTimeslotSize={packageRespectsTimeslotSize}
                                            possibleTimeslotIndex={possibleTimeslotIndex}
                                            possibleTimeslotsLength={possibleTimeslots.length}
                                            selectedTimeslot={selectedTimeslot}
                                            showTimeslotOccupancy={true}
                                            timeslot={timeslot}
                                            onClick={() => setSelectedTimeslotHandler(timeslot, selectCustomSlotsEnabled)}
                                            showEventHoursCategory={!selectedPackageCategory ? false : eventCategories.includes(selectedPackageCategory)}
                                        />
                                    );
                                })}
                            {!isLoadingTimeslots && timeslotsAvailable.length === 0 && (
                                <div className={"w-full flex flex-col items-center justify-center col-span-3 sm:col-span-4 md:col-span-6"}>
                                    <div className={"max-w-[326px] lg:max-w-[640px]"}>
                                        <h3 className={"text-lg font-medium"}>
                                            {t("Sorry")}, {t("completely booked out on this day")}
                                        </h3>
                                        <span>{t("Please choose another date")}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {selectedTimeslot && (
                        <div
                            className={
                                "flex flex-col justify-center items-center h-auto mt-2 mb-1 sticky inset-x-0 w-full bottom-0 bg-gray-100 z-40 rounded-md"
                            }
                        >
                            <div className={"ConfirmTimeslotButton grid justify-center items-center gap-4 sm:bottom-auto z-40"}>
                                <div ref={scrollRefConfirmTimeslots} className={"flex flex-row p-3 w-full inset-x-0"}>
                                    <button
                                        className={clsx(
                                            !selectedTimeslot && !selectCustomSlotsEnabled && blockedClass,
                                            "bg-cta-800 text-contrast-800 cursor-pointer font-medium hover:no-underline text-lg h-14 xl:h-16 sm:text-xl flex-grow text-center px-3 py-2 sm:px-5 sm:py-3 flex flex-row justify-center rounded-lg items-center"
                                        )}
                                        type={"button"}
                                        onClick={!isFetchingTimeslot && selectedTimeslot ? () => prependOoohWarningToBooking(selectedTimeslot) : () => false}
                                    >
                                        <div className={"flex flex-row items-center space-x-1 sm:space-x-2 text-white"}>
                                            <ClockIcon className={"w-5 h-5 flex-none"} />
                                            <span className={"leading-4 sm:leading-7"}>{t("Continue with selected time")}</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </BookingCard>
    );
};

export default withRouter(SellerTimeslots);
